import DropdownCalendarFilter from "@/components/atoms/DropdownCalendarFilter";
import Loader from "@/components/atoms/Loader";
import { CALENDAR_VIEWS } from "@/constants/calendar";
import { ISelectOption } from "@/interfaces/Select";
import {
  faArrowLeft,
  faArrowRight,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CalendarApi,
  DatesSetArg,
  EventClickArg,
  EventInput,
  FormatterInput,
  MoreLinkArg,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { DateTime } from "luxon";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";

export interface CalendarBoardProps {
  /** Identifies the calendar in localStorage, so each one remembers its own
   *  view (e.g. "commercial", "montage"). */
  name: string;
  events: EventInput[];
  isLoading: boolean;
  isFetching: boolean;
  onEventClick: (info: EventClickArg) => void;
  onDayModalRowClick: (event: EventInput) => void;
  /** Clic sur un créneau horaire (vues semaine et jour) : la page ouvre la
   *  création de rendez-vous à cette heure-là. */
  onSlotClick?: (start: Date) => void;
  onDatesSet?: (arg: DatesSetArg) => void;
  /** Own Col in the header row (e.g. the commercial calendar's search box). */
  headerSearch?: ReactNode;
  /** Rendered inside the same trailing Col as the view switcher, before it
   *  (e.g. the commercial calendar's filter button) — kept in the same flex
   *  group so both stay flush together at the row's right edge. */
  headerActions?: ReactNode;
  /** Optional contextual legend rendered below the calendar controls. */
  legend?: ReactNode;
}

const eventTimeFormat: FormatterInput = {
  hour: "numeric",
  minute: "2-digit",
  omitZeroMinute: true,
  meridiem: true,
};

// The chosen view (mois, semaine…) survives reloads. Anything unreadable or no
// longer offered falls back to the month view.
const readStoredView = (storageKey: string): ISelectOption<string> => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(storageKey);
  } catch {
    // Storage blocked (private browsing…): keep the default view.
  }
  return (
    CALENDAR_VIEWS.find((option) => option.value === stored) ??
    CALENDAR_VIEWS[0]
  );
};

// Shared chrome (header nav, view switcher, FullCalendar body, day-overflow
// modal) for every calendar page — Calendrier commercial and Calendrier
// montage must look pixel-identical, so this is the one component both
// render instead of each keeping its own copy.
const CalendarBoard: FC<CalendarBoardProps> = ({
  name,
  events,
  isLoading,
  isFetching,
  onEventClick,
  onDayModalRowClick,
  onSlotClick,
  onDatesSet,
  headerSearch,
  headerActions,
  legend,
}) => {
  const storageKey = `${name}CalendarView`;
  // FullCalendar only reads initialView at mount, so it is frozen here and
  // later switches go through calendarApi.changeView.
  const [initialView] = useState(() => readStoredView(storageKey));
  const [currentView, setCurrentView] =
    useState<ISelectOption<string>>(initialView);
  const CalendarRef = useRef<FullCalendar>(null);
  const [calendarApi, setCalendarApi] = useState<CalendarApi>();
  const [dayModal, setDayModal] = useState<{
    date: string;
    events: EventInput[];
  } | null>(null);
  const [title, setTitle] = useState<string>("");

  useEffect(() => {
    if (!isLoading && CalendarRef.current) {
      setCalendarApi(CalendarRef.current.getApi());
    }
  }, [CalendarRef.current, isLoading]);

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setTitle(arg.view.title.toUpperCase());
      onDatesSet?.(arg);
    },
    [onDatesSet],
  );

  const onChangeView = (view: ISelectOption<string>) => {
    setCurrentView(view);
    calendarApi?.changeView(view.value);
    try {
      localStorage.setItem(storageKey, view.value);
    } catch {
      // Storage blocked: the view still switches, it just won't be remembered.
    }
  };

  const onPrev = () => calendarApi?.prev();
  const onNext = () => calendarApi?.next();
  const onToday = () => calendarApi?.today();

  const openDayModal = (dateStr: string) => {
    const dayStart = DateTime.fromISO(dateStr).startOf("day");
    const dayEnd = dayStart.plus({ days: 1 });
    const dayEvents = events
      .filter((e) => {
        if ((e.extendedProps as any)?.isHoliday) return false;
        const start = e.start as string | undefined;
        if (!start) return false;
        const eventStart = DateTime.fromISO(start);
        const eventEnd = e.end
          ? DateTime.fromISO(e.end as string)
          : eventStart.plus({ hours: 1 });
        return eventStart < dayEnd && eventEnd > dayStart;
      })
      .sort(
        (first, second) =>
          DateTime.fromISO(first.start as string).toMillis() -
          DateTime.fromISO(second.start as string).toMillis(),
      );
    if (dayEvents.length > 0) {
      setDayModal({ date: dateStr, events: dayEvents });
    }
  };

  // En vue semaine ou jour, le clic vise une heure précise : on y crée un
  // rendez-vous. Une case « journée » (vue mois, bandeau du haut) liste les
  // rendez-vous du jour, comme avant.
  const onDateClick = (arg: DateClickArg) => {
    if (arg.allDay) {
      openDayModal(arg.dateStr);
    } else {
      onSlotClick?.(arg.date);
    }
  };

  // "+N autres" opens our own day modal instead of FullCalendar's cramped
  // popover. Going through moreLinkClick rather than intercepting the DOM keeps
  // mouse and keyboard on the same path, since FullCalendar routes both here.
  //
  // The sentinel return is what suppresses the popover. FullCalendar reads the
  // handler's return value as `if (!action || action === 'popover') { open
  // popover } else if (typeof action === 'string') { switch view }` — so
  // returning nothing reopens the popover, and any real string navigates away.
  // A truthy non-string falls through both branches and leaves the click to us.
  const onMoreLinkClick = (arg: MoreLinkArg) => {
    openDayModal(DateTime.fromJSDate(arg.date).toFormat("yyyy-MM-dd"));
    return true as unknown as string;
  };

  // La page décide quoi ouvrir : la fiche entreprise, ou la petite fiche d'un
  // rendez-vous sans entreprise (spec 2026-10-08).
  const onModalRowClick = (event: EventInput) => {
    setDayModal(null);
    onDayModalRowClick(event);
  };

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="align-items-center gx-0">
            <Col xs="auto" className="d-flex justify-content-end order-md-1">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="nextTooltip">Précédent</Tooltip>}
              >
                <Button
                  variant="link"
                  className="icon-item icon-item-sm icon-item-hover shadow-none p-0 me-1 ms-md-2"
                  onClick={onPrev}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="previousTooltip">Suivant</Tooltip>}
              >
                <Button
                  variant="link"
                  className="icon-item icon-item-sm icon-item-hover shadow-none p-0 me-lg-2"
                  onClick={onNext}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </OverlayTrigger>
            </Col>
            <Col xs="auto" className="d-flex justify-content-end order-md-2">
              <h4 className="mb-0 fs-0 fs-sm-1 fs-lg-2">{title}</h4>
            </Col>
            <Col xs md="auto" className="d-flex justify-content-end order-md-3">
              <Button size="sm" variant="nolina-primary" onClick={onToday}>
                Aujourd'hui
              </Button>
            </Col>
            <Col md="auto" className="d-md-none">
              <hr />
            </Col>
            {headerSearch}
            <Col className="d-flex justify-content-end order-md-3">
              {headerActions}
              <DropdownCalendarFilter
                currentView={currentView}
                setCurrentView={onChangeView}
              />
            </Col>
          </Row>
          {legend}
          {/* Règle métier appliquée automatiquement côté serveur chaque
              dimanche : elle déplace de vrais rendez-vous, donc elle doit être
              annoncée là où on les consulte, pas seulement documentée. */}
          <p className="d-flex align-items-center gap-2 mb-0 mt-2 fs--1 text-600">
            <FontAwesomeIcon icon={faCircleInfo} />
            Les rendez-vous du dimanche sont automatiquement reportés au
            dimanche suivant, chaque dimanche.
          </p>
        </Card.Header>
        <Card.Body className="p-0 position-relative">
          <FullCalendar
            ref={CalendarRef}
            headerToolbar={false}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            locale={"fr"}
            firstDay={1}
            moreLinkText={"autres"}
            initialView={initialView.value}
            themeSystem="bootstrap"
            dayMaxEvents={2}
            // Montrer toute occupation après minuit ; une fin à minuit reste exclusive.
            nextDayThreshold="00:00"
            height={800}
            eventMinHeight={100}
            stickyHeaderDates={false}
            selectable
            selectMirror
            eventTimeFormat={eventTimeFormat}
            eventClick={onEventClick}
            dateClick={onDateClick}
            moreLinkClick={onMoreLinkClick}
            datesSet={handleDatesSet}
            events={events}
            eventDidMount={(info) => {
              info.el.setAttribute("title", info.event.title);
            }}
          />
          {(isLoading || isFetching) && (
            <div
              className="position-absolute top-0 start-0 end-0 bottom-0 d-flex justify-content-center align-items-center"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.65)",
                zIndex: 10,
              }}
            >
              <Loader />
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={!!dayModal}
        onHide={() => setDayModal(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {dayModal &&
              DateTime.fromISO(dayModal.date)
                .setLocale("fr")
                .toFormat("d MMMM yyyy")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dayModal?.events.length ? (
            <div className="d-flex flex-column gap-2">
              {dayModal.events.map((e, idx) => {
                const start = e.start as string | undefined;
                const time = start
                  ? DateTime.fromISO(start).toFormat("HH:mm")
                  : "";
                const companyId = (e as any).companyId as string | undefined;
                const isRestricted = Boolean((e as any).restricted);
                const details = (e as any).details as
                  { label: string; value: string }[] | undefined;
                const isStandalone = Boolean((e as any).standalone);
                const isInteractive =
                  (Boolean(companyId) || isStandalone) && !isRestricted;
                return (
                  <div
                    key={(e as any).id ?? `${start}-${idx}`}
                    role={
                      isInteractive
                        ? "button"
                        : isRestricted
                          ? "group"
                          : undefined
                    }
                    aria-label={
                      isRestricted ? `Créneau occupé, ${time}` : undefined
                    }
                    tabIndex={isInteractive ? 0 : undefined}
                    onClick={
                      isInteractive ? () => onModalRowClick(e) : undefined
                    }
                    onKeyDown={
                      isInteractive
                        ? (ev) => {
                            if (ev.key === "Enter" || ev.key === " ") {
                              ev.preventDefault();
                              onModalRowClick(e);
                            }
                          }
                        : undefined
                    }
                    className="d-flex align-items-center p-2 border rounded"
                    style={{
                      cursor: isInteractive ? "pointer" : "default",
                      backgroundColor:
                        (e.backgroundColor as string) || undefined,
                      color: (e.textColor as string) || undefined,
                    }}
                  >
                    <span className="me-2 fw-bold">{time}</span>
                    {details?.length ? (
                      // Le montage fournit `details` : on déplie le bloc étiqueté.
                      // Sans lui (calendrier commercial, jours fériés), on garde
                      // le simple titre d'origine.
                      <div className="d-flex flex-column">
                        <span className="fw-semi-bold">
                          {(e as any).headline ?? (e.title as string)}
                        </span>
                        {details.map((detail) => (
                          <span key={detail.label} className="fs--1">
                            <span className="opacity-75 me-1">
                              {detail.label}
                            </span>
                            <span>{detail.value}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span>{e.title as string}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted text-center mb-0">
              Aucun rendez-vous ce jour
            </p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CalendarBoard;
